import { useMemo, useState } from "react";
import {
  ALLEGRO_COLUMN_SPECS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  FILTER_COLUMN_IDS,
} from "../components/columnSpecs";
import { useColumnPreferences } from "../../../components/table/useColumnPreferences";
import { useColumnResizing } from "../../../components/table/useColumnResizing";

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 480;

export const COLUMN_DENSITY_LEVELS = [
  { label: "Micro", headerPadding: "px-1 py-1 text-xs", cellPadding: "px-1 py-1 text-xs" },
  { label: "Slim", headerPadding: "px-2 py-2 text-xs", cellPadding: "px-2 py-2 text-xs" },
  { label: "Compact", headerPadding: "px-4 py-3 text-sm", cellPadding: "px-4 py-3 text-sm" },
  { label: "Cozy", headerPadding: "px-6 py-4 text-base", cellPadding: "px-6 py-4 text-base" },
];

export const INITIAL_FILTERS = {
  search: "",
  status: "",
  fulfillmentStatus: "",
  deliveryMethod: "",
  buyerLogin: "",
  smart: "",
};

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null) return [];
  return [value];
};

const stringifyValue = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  const text = String(value);
  return text.trim().length ? text : "";
};

const getOrderPayload = (order) =>
  order?.allegro_payload || order?.allegroPayload || order?.allegroData || null;

const getValuesFromPath = (source, path) => {
  if (!source || !path) return [];
  const segments = path
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean);
  let current = [source];
  for (const segment of segments) {
    if (!current.length) break;
    const next = [];
    const isSpread = segment.endsWith("[]");
    const cleanSegment = isSpread ? segment.slice(0, -2) : segment;
    for (const item of current) {
      if (item === undefined || item === null) continue;
      let target = item;
      let key = cleanSegment;
      let index = null;
      const matchIndex = cleanSegment.match(/(.*)\[(\d+)\]$/);
      if (matchIndex) {
        key = matchIndex[1];
        index = parseInt(matchIndex[2], 10);
      }
      if (key) target = target?.[key];
      if (index !== null) {
        if (Array.isArray(target) && target.length > index) {
          target = target[index];
        } else {
          target = undefined;
        }
      }
      if (isSpread) {
        if (Array.isArray(target)) {
          next.push(...target);
        } else if (target !== undefined && target !== null) {
          next.push(target);
        }
      } else {
        next.push(target);
      }
    }
    current = next;
  }
  return current.filter((value) => value !== undefined && value !== null);
};

const computeSpecCell = (order, rowIndex, spec) => {
  let values = [];
  if (typeof spec.getter === "function") {
    values = ensureArray(spec.getter(order, rowIndex));
  } else if (spec.path) {
    values = getValuesFromPath(getOrderPayload(order), spec.path);
  }
  if ((!values || values.length === 0) && typeof spec.fallback === "function") {
    values = ensureArray(spec.fallback(order, rowIndex));
  }
  if (!Array.isArray(values)) values = [];
  const strings = values.map(stringifyValue).filter((text) => text !== "");
  const display = strings.length ? strings.join(", ") : "-";
  return { raw: values, strings, display };
};

const uniqueNonEmpty = (values) => {
  const acc = new Set();
  values.forEach((value) => {
    if (value === undefined || value === null) return;
    const text = String(value).trim();
    if (text) acc.add(text);
  });
  return Array.from(acc).sort((a, b) => a.localeCompare(b));
};

export const useOrderTableState = (orders) => {
  const allColumnIds = useMemo(() => ALLEGRO_COLUMN_SPECS.map((spec) => spec.id), []);
  const columnPrefs = useColumnPreferences({
    allColumnIds,
    defaultVisibleIds: DEFAULT_VISIBLE_COLUMN_IDS,
    storageKey: "orders.visibleColumns",
  });

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [densityLevel, setDensityLevel] = useState(2);

  const columnSizing = useColumnResizing({
    minWidth: MIN_COLUMN_WIDTH,
    maxWidth: MAX_COLUMN_WIDTH,
  });

  const tableRows = useMemo(() => {
    const safeOrders = Array.isArray(orders) ? orders : [];
    return safeOrders.map((order, index) => {
      const values = {};
      ALLEGRO_COLUMN_SPECS.forEach((spec) => {
        values[spec.id] = computeSpecCell(order, index, spec);
      });
      return { order, index, values };
    });
  }, [orders]);

  const visibleColumnDefs = useMemo(
    () => ALLEGRO_COLUMN_SPECS.filter((spec) => columnPrefs.visibleColumns.includes(spec.id)),
    [columnPrefs.visibleColumns],
  );

  const columnOptions = useMemo(
    () => ALLEGRO_COLUMN_SPECS.map((spec) => ({ id: spec.id, label: spec.label })),
    [],
  );

  const filterOptions = useMemo(() => {
    const statusOptions = uniqueNonEmpty(
      tableRows.map((row) => row.values[FILTER_COLUMN_IDS.status]?.strings?.[0]),
    );
    const fulfillmentOptions = uniqueNonEmpty(
      tableRows.map((row) => row.values[FILTER_COLUMN_IDS.fulfillmentStatus]?.strings?.[0]),
    );
    const deliveryOptions = uniqueNonEmpty(
      tableRows.map((row) => row.values[FILTER_COLUMN_IDS.deliveryMethod]?.strings?.[0]),
    );
    const buyerLoginOptions = uniqueNonEmpty(
      tableRows.map((row) => row.values[FILTER_COLUMN_IDS.buyerLogin]?.strings?.[0]),
    );
    const smartOptions = uniqueNonEmpty(
      tableRows.map((row) => row.values[FILTER_COLUMN_IDS.smart]?.strings?.[0]),
    );
    return { statusOptions, fulfillmentOptions, deliveryOptions, buyerLoginOptions, smartOptions };
  }, [tableRows]);

  const filteredRows = useMemo(() => {
    const searchTerm = filters.search.trim().toLowerCase();
    return tableRows.filter((row) => {
      const statusValue = row.values[FILTER_COLUMN_IDS.status]?.strings?.[0] || "";
      if (filters.status && statusValue !== filters.status) return false;

      const fulfillmentValue = row.values[FILTER_COLUMN_IDS.fulfillmentStatus]?.strings?.[0] || "";
      if (filters.fulfillmentStatus && fulfillmentValue !== filters.fulfillmentStatus) return false;

      const deliveryValue = row.values[FILTER_COLUMN_IDS.deliveryMethod]?.strings?.[0] || "";
      if (filters.deliveryMethod && deliveryValue !== filters.deliveryMethod) return false;

      const buyerLoginValue = row.values[FILTER_COLUMN_IDS.buyerLogin]?.strings?.[0] || "";
      if (filters.buyerLogin && buyerLoginValue !== filters.buyerLogin) return false;

      const smartValue = row.values[FILTER_COLUMN_IDS.smart]?.strings?.[0] || "";
      if (filters.smart && smartValue !== filters.smart) return false;

      if (!searchTerm) return true;
      const corpus = Object.values(row.values)
        .flatMap((entry) => entry.strings)
        .join(" ")
        .toLowerCase();
      return corpus.includes(searchTerm);
    });
  }, [tableRows, filters]);

  const safeDensityIndex = Math.min(Math.max(densityLevel, 0), COLUMN_DENSITY_LEVELS.length - 1);
  const densityConfig = COLUMN_DENSITY_LEVELS[safeDensityIndex];

  return {
    columnPrefs,
    columnSizing,
    columnOptions,
    density: {
      level: densityLevel,
      setLevel: setDensityLevel,
      headerPaddingClass: densityConfig.headerPadding,
      cellPaddingClass: densityConfig.cellPadding,
      label: densityConfig.label,
    },
    filterOptions,
    filters,
    resetFilters: () => setFilters(INITIAL_FILTERS),
    setFilters,
    tableRows,
    filteredRows,
    visibleColumnDefs,
  };
};
